import { getRouteApi } from '@tanstack/react-router';
import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { AGGREGATE_NAMES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { RxBookmark, RxBookmarkFilled } from 'react-icons/rx';

import { useTeaserOffers } from '../../../hooks/useTeaserOffers';
import TreatButton from '~/components/TreatButton';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { cn } from '~/utils/cnTailwind';

type Props = {
    acceptedOffer: SchemaLoanRequestResolver_GetAcceptedOffers[number];
    withLabel?: boolean;
};

export const Teaser = ({ acceptedOffer, withLabel = false }: Props) => {
    const { t } = useTranslation();
    const { id, loanRequestId: aggregateId, teaser } = acceptedOffer;
    const teaserOffer = useTeaserOffers({ aggregateId, id, teaser: !teaser });
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const isDisabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].changeOfferTeaserState;
    const content = (
        <TreatButton
            data-testid="teaser-offer"
            onClick={(event) => {
                event.stopPropagation();
                teaserOffer.mutate();
            }}
            type="button"
            className={cn(
                'flex items-center gap-2 py-1.5 px-3',
                (isDisabled || teaserOffer.isPending) && 'opacity-50 cursor-not-allowed',
            )}
            disabled={isDisabled || teaserOffer.isPending}
        >
            {teaser ? <RxBookmarkFilled size={16} color="0098CC" /> : <RxBookmark size={16} />}
            {withLabel && <span className="text-xs px-3">{t('OFFER_MANAGEMENT.ICONS_TOOLTIP.TEASER')}</span>}
        </TreatButton>
    );

    if (withLabel) {
        return content;
    }

    return (
        <Tooltip>
            <TooltipTrigger>{content}</TooltipTrigger>
            <TooltipContent>{t('OFFER_MANAGEMENT.ICONS_TOOLTIP.TEASER')}</TooltipContent>
        </Tooltip>
    );
};
